import React, {
  CSSProperties,
  forwardRef,
  InputHTMLAttributes,
  KeyboardEventHandler,
  ReactNode,
  SyntheticEvent,
} from 'react';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import colors from '../../constants/colors.constants';

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  placeholder?: string;
  value?: string;
  onChange?: (value: string, event: SyntheticEvent) => void;
  style?: CSSProperties;
  numberPad?: boolean;
  onKeydown?: KeyboardEventHandler;
  icon?: ReactNode;
  borderless?: boolean;
}
const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    onKeydown,
    numberPad = false,
    style,
    value,
    placeholder,
    onChange,
    children,
    icon,
    borderless = false,
    ...rest
  } = props;

  let extraProps = {};
  if (numberPad) {
    extraProps = {
      type: 'tel',
      inputMode: 'numeric',
      patter: '[0-9]*',
    };
  }

  const InputElement = (
    <StyledInput
      onKeyDown={onKeydown}
      $isMobile={isMobile}
      $activeColor={colors.primary3}
      $borderDefaultColor={colors.greyHover}
      style={style}
      value={value}
      placeholder={placeholder}
      ref={ref}
      $borderless={borderless}
      onChange={(e) => {
        const { target } = e;
        const { value } = target;
        if (onChange) {
          onChange(value, e);
        }
      }}
      {...extraProps}
      {...rest}
    />
  );

  if (icon) {
    return (
      <div className="flex items-center" style={style}>
        {icon}
        {InputElement}
      </div>
    );
  }

  return InputElement;
});

export default Input;

const StyledInput = styled.input<{
  $borderDefaultColor: string;
  $activeColor: string;
  $isMobile: boolean;
  $borderless: boolean;
}>`
  height: ${(props) => {
    if (props.$isMobile) {
      return '48px';
    }
    return '40px';
  }};
  padding: 0 16px;
  font-size: 16px;
  font-weight: 300;
  font-family: Pretendard;

  border: ${(props) => {
    if (props.$borderless) {
      return 'none';
    }

    return `1px solid ${props.$borderDefaultColor}`;
  }};

  &:focus {
    outline: none;
    border: ${(props) => {
      if (props.$borderless) {
        return 'none';
      }
      return `1px solid ${props.$activeColor}`;
    }};
  }

  &:hover {
    border: ${(props) => {
      if (props.$borderless) {
        return 'none';
      }
      return `1px solid ${props.$activeColor}`;
    }};
  }

  transition: border 0.2s;
`;
